import axios from 'axios';
import { CONFIG } from '../config/constants';

// Define API base URL from constants
const API_URL = CONFIG.API.API_URL;

// Define types
export interface Question {
  id: number;
  document_id: number;
  text: string;
  options: string[];
  answer_objects?: Array<{
    id: string;
    text: string;
  }>;
  correct_answer: number;
  correct_answer_id?: string;
  explanation: string | null;
  type: 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_in_blank';
  difficulty: number | null;
  tags: string[] | null;
  ai_question_id?: string;
  created_at: string;
  updated_at: string;
  document?: {
    id: number;
    name: string;
  };
}

export interface QuestionListResponse {
  success: boolean;
  document_id: number;
  document_name: string;
  processed: boolean;
  processing_status: string;
  questions_count: number;
  questions: Question[];
}

export interface ProcessingResponse {
  success: boolean;
  message: string;
  document_id: number;
  ai_document_id?: string;
}

export interface StatusResponse {
  success: boolean;
  document_id: number;
  processing_status: string;
  processed: boolean;
  status_details?: any;
}

export interface QuestionPaginatedResponse {
  data: {
    data: Question[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface CreateQuestionData {
  document_id: number;
  text: string;
  options: string[];
  answer_objects?: Array<{
    id: string;
    text: string;
  }>;
  correct_answer: number;
  correct_answer_id?: string;
  explanation?: string;
  type?: 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_in_blank';
  difficulty?: number;
  tags?: string[];
}

export interface UpdateQuestionData {
  text?: string;
  options?: string[];
  answer_objects?: Array<{
    id: string;
    text: string;
  }>;
  correct_answer?: number | undefined;
  correct_answer_id?: string;
  explanation?: string;
  type?: 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_in_blank';
  difficulty?: number;
  tags?: string[];
}

export interface SearchFilters {
  query: string;
  type?: 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_in_blank';
  difficulty_min?: number;
  difficulty_max?: number;
  document_id?: number;
}

// Configure axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Accept': 'application/json',
  },
});

// Add a request interceptor to include the token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Question service
const questionService = {
  // Get questions for a document
  getQuestions: async (documentId: number): Promise<QuestionListResponse> => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/questions`);
      return {
        success: true,
        document_id: response.data.document_id,
        document_name: response.data.document_name,
        processed: response.data.processed,
        processing_status: response.data.processing_status,
        questions_count: response.data.questions_count,
        questions: response.data.questions
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy câu hỏi cho tài liệu');
    }
  },

  // Process a document to generate questions
  processDocument: async (documentId: number, numQuestions: number = 10): Promise<ProcessingResponse> => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/process-async`, {
        num_questions: numQuestions
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xử lý tài liệu');
    }
  },

  // Check the status of document processing
  checkProcessingStatus: async (documentId: number): Promise<StatusResponse> => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/status`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể kiểm tra trạng thái xử lý');
    }
  },

  // Get all questions for authenticated user (paginated)
  getAllQuestions: async (page: number = 1, perPage: number = 20): Promise<QuestionPaginatedResponse> => {
    try {
      const response = await apiClient.get(`/questions?page=${page}&per_page=${perPage}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch questions');
    }
  },

  // Get a specific question
  getQuestion: async (questionId: number): Promise<Question> => {
    try {
      const response = await apiClient.get(`/questions/${questionId}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy thông tin câu hỏi');
    }
  },

  // Create a new question
  createQuestion: async (data: CreateQuestionData): Promise<Question> => {
    try {
      const response = await apiClient.post('/questions', data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể tạo câu hỏi mới');
    }
  },

  // Update a question
  updateQuestion: async (questionId: number, data: UpdateQuestionData): Promise<Question> => {
    try {
      const response = await apiClient.put(`/questions/${questionId}`, data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể cập nhật câu hỏi');
    }
  },

  // Delete a question
  deleteQuestion: async (questionId: number): Promise<void> => {
    try {
      await apiClient.delete(`/questions/${questionId}`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xóa câu hỏi');
    }
  },

  // Bulk delete questions
  bulkDeleteQuestions: async (questionIds: number[]): Promise<void> => {
    try {
      await apiClient.delete('/questions', {
        data: { question_ids: questionIds }
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xóa các câu hỏi');
    }
  },

  // Search questions
  searchQuestions: async (filters: SearchFilters, page: number = 1): Promise<QuestionPaginatedResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('query', filters.query);
      params.append('page', page.toString());
      
      if (filters.type) params.append('type', filters.type);
      if (filters.difficulty_min) params.append('difficulty_min', filters.difficulty_min.toString());
      if (filters.difficulty_max) params.append('difficulty_max', filters.difficulty_max.toString());
      if (filters.document_id) params.append('document_id', filters.document_id.toString());

      const response = await apiClient.get(`/questions/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể tìm kiếm câu hỏi');
    }
  },

  // Get questions by document
  getQuestionsByDocument: async (documentId: number): Promise<Question[]> => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/questions`);
      return response.data.questions || [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy danh sách câu hỏi cho tài liệu');
    }
  }
};

export default questionService; 